/* ============================================================
 * Swandrops — Quiz « Trouve ton fond idéal »
 * Vanilla JS state machine + URL builder.
 *
 * Mapping vers facettes Search & Discovery :
 *   Q1 (sujet)     → custom.th_me
 *   Q2 (ambiance)  → custom.style       (multi-valeurs OR)
 *   Q2-bis (event) → custom.ev_nement
 *   Q3 (couleur)   → tag produit         (multi-valeurs OR)
 *
 * Collection cible : /collections/nos-fonds-photos
 * ============================================================ */

(function () {
  'use strict';

  const TARGET_COLLECTION = '/collections/nos-fonds-photos';

  // ── Mapping réponses → valeurs Shopify ──
  // IMPORTANT : Shopify applique un AND entre valeurs d'un même metafield.
  // Donc on n'envoie qu'UNE seule valeur par bucket pour les metafields.
  // (Les tags couleur restent multi-valeurs car ils sont en OR.)
  const THEME_MAP = {
    grossesse: ['Grossesse'],
    bebe: ['Bébé'],
    enfant: ['Enfant'],
    portrait: ['Portrait'], // "Boudoir" retiré (AND vide). Les Boudoir ont quasi tous "Portrait" aussi.
    // evenement → pas de filtre Thème, on bascule vers Q2-bis
  };

  const STYLE_MAP = {
    nature:   ['Fleurs'],         // valeur la plus peuplée du bucket (254). Champêtre/Forêt retirés (AND vide).
    boheme:   ['Bohème'],         // Ambiance Bohème retiré (AND vide). La majorité a "Bohème".
    archi:    ['Intérieur chic'], // Briques retiré (AND vide). Intérieur chic est plus large (94 vs 28).
    onirique: ['Féérique'],       // Poupée/Ailes retirés (AND vide). Féérique = 61, le plus peuplé.
    texture:  ['Texturés'],
  };

  const EVENT_MAP = {
    noel:      'Noël',
    halloween: 'Halloween',
    valentin:  'Saint-Valentin',
    fete:      'Fêtes et anniversaires',
    smashcake: 'Smash cake',
    rentree:   'Rentrée scolaire',
  };

  const COLOR_MAP = {
    clair:  ['Blanc', 'Or', 'Gris'],
    pastel: ['Rose', 'Violet'],
    froid:  ['Bleu', 'Vert'],
    chaud:  ['Rouge', 'Orange', 'Jaune', 'Marron'],
    fonce:  ['Noir', 'Marron'],
    // multi → pas de filtre couleur
  };

  // ── Count matrix (fallback) ──
  let countMatrix = {};
  fetch(window.SwanQuizCountsUrl).then(r => r.json()).then(j => { countMatrix = j; }).catch(() => {});

  function lookupCount(key) {
    return countMatrix[key] ?? null;
  }

  // ── State ──
  let state = { q1: null, q2: null, q2b: null, q3: null };
  let modal = null;

  function resetState() {
    state = { q1: null, q2: null, q2b: null, q3: null };
  }

  // ── Analytics ──
  // GA4 tourne en Web Pixel sandboxé (canal Google). On publie sur le bus
  // Customer Events ; un Custom Pixel (admin) relaie vers GA4 G-XJL9GT9F8E.
  function track(name, data) {
    try {
      window.Shopify?.analytics?.publish(name, data || {});
    } catch (e) {}
  }

  // ── URL builder ──
  function buildResultUrl() {
    const params = new URLSearchParams();

    // Q1 → Thème (sauf "evenement")
    if (state.q1 && THEME_MAP[state.q1]) {
      THEME_MAP[state.q1].forEach(v => params.append('filter.p.m.custom.th_me', v));
    }
    // Q2 → Style (uniquement si Q1 ≠ evenement)
    if (state.q2 && STYLE_MAP[state.q2]) {
      STYLE_MAP[state.q2].forEach(v => params.append('filter.p.m.custom.style', v));
    }
    // Q2-bis → Évènement
    if (state.q2b && EVENT_MAP[state.q2b]) {
      params.append('filter.p.m.custom.ev_nement', EVENT_MAP[state.q2b]);
    }
    // Q3 → couleur (tag)
    if (state.q3 && COLOR_MAP[state.q3]) {
      COLOR_MAP[state.q3].forEach(v => params.append('filter.p.tag', v));
    }

    const qs = params.toString();
    return qs ? `${TARGET_COLLECTION}?${qs}` : TARGET_COLLECTION;
  }

  // ── Screen navigation ──
  function showScreen(name) {
    if (!modal) return;
    modal.querySelectorAll('.q-screen').forEach(s => s.classList.remove('is-active'));
    const target = modal.querySelector(`.q-screen[data-screen="${name}"]`);
    if (target) target.classList.add('is-active');
    updateProgress(name);
    updateBackButton(name);
  }

  function updateProgress(screenName) {
    const stepMap = { q1: 1, q2: 2, q2b: 2, q3: 3, loader: 3 };
    const currentStep = stepMap[screenName] || 1;
    modal.querySelectorAll('.q-dots').forEach(dots => {
      dots.querySelectorAll('i').forEach((dot, idx) => {
        const n = idx + 1;
        dot.classList.remove('active', 'done');
        if (n === currentStep) dot.classList.add('active');
        else if (n < currentStep) dot.classList.add('done');
      });
    });
  }

  function updateBackButton(screenName) {
    const backBtn = modal.querySelector('[data-quiz-back]');
    if (!backBtn) return;
    backBtn.style.visibility = (screenName === 'q1') ? 'hidden' : 'visible';
  }

  // ── Option click handler ──
  function selectOption(screen, key) {
    // Visually mark selected within current screen
    const screenEl = modal.querySelector(`.q-screen[data-screen="${screen}"]`);
    if (screenEl) {
      screenEl.querySelectorAll('.opt').forEach(o => o.classList.remove('is-selected'));
      const target = screenEl.querySelector(`.opt[data-key="${key}"]`);
      if (target) target.classList.add('is-selected');
    }

    track('quiz_question_answered', { question: screen, answer: key });

    // Update state + advance
    if (screen === 'q1') {
      state.q1 = key;
      if (key === 'evenement') {
        setTimeout(() => showScreen('q2b'), 220);
      } else {
        setTimeout(() => showScreen('q2'), 220);
      }
    } else if (screen === 'q2') {
      state.q2 = key;
      setTimeout(() => showScreen('q3'), 220);
    } else if (screen === 'q2b') {
      state.q2b = key;
      setTimeout(() => showScreen('q3'), 220);
    } else if (screen === 'q3') {
      state.q3 = key;
      // Enable CTA
      const cta = modal.querySelector('[data-quiz-submit]');
      if (cta) cta.disabled = false;
    }
  }

  // ── Back navigation ──
  function goBack() {
    const active = modal.querySelector('.q-screen.is-active');
    if (!active) return;
    const current = active.dataset.screen;
    if (current === 'q2' || current === 'q2b') {
      showScreen('q1');
    } else if (current === 'q3') {
      showScreen(state.q1 === 'evenement' ? 'q2b' : 'q2');
    }
  }

  // ── Submit (Q3 → loader → redirect) ──
  function submit() {
    showScreen('loader');
    const url = buildResultUrl();
    setTimeout(() => { window.location.href = url; }, 1200);
  }

  function submitWithFallback() {
    showScreen('loader');

    const answers = { q1: state.q1, q2: state.q2, q2b: state.q2b, q3: state.q3 };
    const THRESHOLD = 5;
    const isPathB = state.q1 === 'evenement';
    let fallbackUsed = null;

    if (isPathB) {
      const key = `B|${state.q2b}|${state.q3 ?? '*'}`;
      const count = lookupCount(key);
      if (count !== null && count < THRESHOLD) {
        state.q3 = null;
        fallbackUsed = 'color';
      }
    } else {
      const key = `A|${state.q1}|${state.q2}|${state.q3 ?? '*'}`;
      const count = lookupCount(key);
      if (count !== null && count < THRESHOLD) {
        state.q3 = null;
        fallbackUsed = 'color';
        const k2 = `A|${state.q1}|${state.q2}|*`;
        const c2 = lookupCount(k2);
        if (c2 !== null && c2 < THRESHOLD) {
          state.q2 = null;
          fallbackUsed = 'color+style';
        }
      }
    }

    const url = buildResultUrl();
    const finalUrl = fallbackUsed
      ? `${url}${url.includes('?') ? '&' : '?'}swan_fb=${encodeURIComponent(fallbackUsed)}`
      : url;

    track('quiz_completed', {
      path: isPathB ? 'B' : 'A',
      q1: answers.q1,
      q2: answers.q2,
      q2b: answers.q2b,
      q3: answers.q3,
      fallback: fallbackUsed || 'none',
      result_url: finalUrl,
    });

    setTimeout(() => { window.location.href = finalUrl; }, 1200);
  }

  // ── Modal open/close ──
  function openModal(preselectQ1, source) {
    if (!modal) return;
    document.body.style.overflow = 'hidden';
    modal.classList.add('is-open');
    track('quiz_started', { source: source || (preselectQ1 ? 'homepage' : 'trigger') });
    if (preselectQ1) {
      state.q1 = preselectQ1;
      selectOption('q1', preselectQ1);
    } else {
      showScreen('q1');
    }
  }

  function closeModal() {
    if (!modal) return;
    modal.classList.remove('is-open');
    document.body.style.overflow = '';
    resetState();
    showScreen('q1');
    modal.querySelectorAll('.opt').forEach(o => o.classList.remove('is-selected'));
    const cta = modal.querySelector('[data-quiz-submit]');
    if (cta) cta.disabled = true;
  }

  // ── Init ──
  function init() {
    modal = document.querySelector('[data-swan-quiz-modal]');
    if (!modal) return;

    // Modal options
    modal.addEventListener('click', (e) => {
      const opt = e.target.closest('.opt[data-key]');
      if (opt) {
        const screen = opt.closest('.q-screen').dataset.screen;
        selectOption(screen, opt.dataset.key);
        return;
      }
      if (e.target.closest('[data-quiz-close]')) {
        closeModal();
        return;
      }
      if (e.target.closest('[data-quiz-back]')) {
        goBack();
        return;
      }
      if (e.target.closest('[data-quiz-submit]')) {
        submitWithFallback();
        return;
      }
    });

    // ESC to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
    });

    // Homepage block options (open modal with Q1 preselected)
    document.querySelectorAll('[data-swan-quiz-entry="homepage"] .opt[data-key]').forEach(opt => {
      opt.addEventListener('click', (e) => {
        e.preventDefault();
        openModal(opt.dataset.key);
      });
    });

    // External triggers (mega menu, buttons, etc.)
    document.querySelectorAll('[data-swan-quiz-trigger]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openModal();
      });
    });
  }

  // Expose API for external triggers (mega menu integration)
  window.SwanQuiz = { open: openModal, close: closeModal };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
